import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  EmptyResponse,
  IncludeQuery,
  FilterByQuery,
  MeResponse,
  PaginationQuery,
  SortByQuery,
  User,
} from "~/types/api";
import { serializeBooleanForFormData } from "./usePatientApi";

export function createAuthApi({ getRequest, postRequest, getPaginationRequest }: ApiRequestHelpers) {
  /** Fetches the current authenticated user's profile. */
  const me = (options?: { omitContextHeaders?: boolean }) => getRequest<MeResponse>("/me", options);

  /** Logs out the current user and invalidates the session. */
  const logout = () => postRequest<EmptyResponse>("/logout", {});

  /** Fetches a paginated list of users. */
  const users = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    return getPaginationRequest<User>("/users", pagination, sortBy, includes, filterBy);
  };

  /** Registers a patient using an invitation code. */
  const registerPatient = (
    invitationCode: string,
    data: {
      first_name: string;
      initials: string;
      last_name: string;
      gender: string;
      birth_date: string;
      citizen_service_number?: string | null;
      postal_code: string;
      housenumber: number;
      housenumber_addition?: string | null;
      email: string;
      phone_number: string;
      password: string;
      password_confirmation: string;
      infix?: string | null;
      nationality?: string | null;
      preferred_language?: string | null;
      identifiers?: Array<{
        type: string;
        value: string;
        country_iso3: string;
        valid_from?: string | null;
        valid_until?: string | null;
        include_on_invoice?: boolean;
      }>;
      accept_terms_and_conditions: boolean;
      accept_privacy_policy: boolean;
    },
  ) => {
    const formData = new URLSearchParams();
    formData.append("first_name", data.first_name);
    formData.append("initials", data.initials);
    formData.append("last_name", data.last_name);
    formData.append("gender", data.gender);
    formData.append("birth_date", data.birth_date);
    formData.append("postal_code", data.postal_code);
    formData.append("housenumber", data.housenumber.toString());
    formData.append("email", data.email);
    formData.append("phone_number", data.phone_number);
    formData.append("password", data.password);
    formData.append("password_confirmation", data.password_confirmation);
    formData.append("accept_terms_and_conditions", String(data.accept_terms_and_conditions));
    formData.append("accept_privacy_policy", String(data.accept_privacy_policy));
    if (data.citizen_service_number) formData.append("citizen_service_number", data.citizen_service_number);
    if (data.housenumber_addition) formData.append("housenumber_addition", data.housenumber_addition);
    if (data.infix) formData.append("infix", data.infix);
    if (data.nationality) formData.append("nationality", data.nationality);
    if (data.preferred_language) formData.append("preferred_language", data.preferred_language);

    data.identifiers?.forEach((identifier, index) => {
      formData.append(`identifiers[${index}][type]`, identifier.type);
      formData.append(`identifiers[${index}][value]`, identifier.value);
      formData.append(`identifiers[${index}][country_iso3]`, identifier.country_iso3);
      if (identifier.valid_from) formData.append(`identifiers[${index}][valid_from]`, identifier.valid_from);
      if (identifier.valid_until) formData.append(`identifiers[${index}][valid_until]`, identifier.valid_until);
      formData.append(
        `identifiers[${index}][include_on_invoice]`,
        serializeBooleanForFormData(identifier.include_on_invoice ?? false),
      );
    });

    return postRequest<void>(`/registration/${invitationCode}`, formData);
  };

  return { me, logout, users, registerPatient };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
